import { RefreshCw } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";

import { useRefreshOrder } from "../api/use-refresh-order";
import { relativeTime } from "../lib/relative-time";

interface OrderRefreshIndicatorProps {
  id: string;
  /**
   * orders:write — when false the whole indicator renders nothing: a viewer
   * can never refresh, so a permanent "Not yet synced" label is noise.
   * Defaults to true.
   */
  canRefresh?: boolean;
}

/**
 * "Synced X ago" indicator + manual Refresh button for the order detail
 * page's Hero row. Manual-only, by design: every GetOrderDetail call shares
 * a per-shop rate limit (internal/marketplace/internal/ratelimit) with the
 * scheduler's poll job and reconciliation, so merely opening an order page
 * must not spend that budget. Data otherwise only updates via the
 * scheduler poll / reconciliation — this button is the one on-demand
 * exception. The synced-at timestamp is client-side-only state (not
 * persisted), reset whenever `id` changes so it never shows a previous
 * order's sync time.
 */
export function OrderRefreshIndicator({
  id,
  canRefresh = true,
}: OrderRefreshIndicatorProps): JSX.Element | null {
  const { t, i18n } = useTranslation("orders");
  const refresh = useRefreshOrder(id);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  // Reset the synced-at display when navigating to a different order,
  // without an effect (React's "adjust state during render" pattern) —
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [syncedForId, setSyncedForId] = useState(id);
  if (id !== syncedForId) {
    setSyncedForId(id);
    setLastSyncedAt(null);
  }

  function runRefresh(): void {
    refresh.mutate(undefined, {
      onSuccess: () => setLastSyncedAt(new Date().toISOString()),
    });
  }

  // After every hook call (rules of hooks): no refresh permission → no indicator.
  if (!canRefresh) return null;

  const err = refresh.error;
  const status = refresh.error instanceof ApiError ? refresh.error.status : null;
  const notConfigured = status === 503;
  // 501 not_implemented is permanent while the direct-marketplace refresh is
  // undelivered — retrying can never succeed, so it must not read as a
  // transient "try again" failure. Same treatment as `useSellerRef`'s
  // `isNotImplemented`: disable the affordance, say "not available yet".
  const notImplemented = status === 501;
  const unavailable = notConfigured || notImplemented;

  const isWarning = refresh.isError;
  const disabled = refresh.isPending || unavailable;
  const label = refresh.isPending
    ? t("refresh_syncing")
    : notImplemented
      ? t("refresh_not_available")
      : notConfigured
        ? t("refresh_not_configured")
        : status === 404
          ? t("refresh_not_found")
          : refresh.isError
            ? t("refresh_failed")
            : lastSyncedAt
              ? t("refresh_synced", { rel: relativeTime(lastSyncedAt, i18n.language) })
              : t("refresh_never");

  return (
    <div className="flex items-center gap-2" data-testid="order-refresh-indicator">
      <span
        className={cn(
          "text-xs",
          isWarning
            ? "font-medium text-[var(--intent-warning-fg,var(--text-primary))]"
            : "text-[var(--text-subtle)]",
        )}
        title={err instanceof ApiError ? err.message : undefined}
      >
        {isWarning ? "⚠ " : ""}
        {label}
      </span>
      <Button
        onClick={runRefresh}
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-label={t("btn_refresh")}
      >
        <RefreshCw aria-hidden className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
      </Button>
    </div>
  );
}
